import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useUpdateServiceBook } from '@/hooks/useChecklists';
import { useNotify } from '@/hooks/useNotify';
import { describeApiError } from '@/lib/describeApiError';

/**
 * Where the checklist runs (#4102):
 * - `ready_to_invoice`: work_completed → ready_to_invoice. Parts and labour
 *   must be confirmed; the final inspection may stay open (a warning says it
 *   is required before hand-over); an unfilled service book is confirmed here.
 * - `invoice`: the Payments card before the invoice is created — the hand-over
 *   step. Only opened while the final inspection is still open, which it then
 *   requires; parts and labour were confirmed at ready-to-invoice.
 */
export type PreInvoiceChecklistStage = 'ready_to_invoice' | 'invoice';

export interface PreInvoiceChecklistServiceBook {
  /** Ask about the service book: the module is on and it is not filled or overridden. */
  pending: boolean;
  /** The book applies and the user cannot override it, so it must be confirmed. */
  required: boolean;
}

export interface PreInvoiceChecklistDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  workOrderId: string;
  finalInspectionCompleted: boolean;
  laborCount: number;
  partsCount: number;
  stage?: PreInvoiceChecklistStage;
  serviceBook?: PreInvoiceChecklistServiceBook | undefined;
  /** Disables continue while the caller's own request runs. */
  submitting?: boolean;
}

export function PreInvoiceChecklistDialog({
  open,
  onClose,
  onConfirm,
  workOrderId,
  finalInspectionCompleted,
  laborCount,
  partsCount,
  stage = 'invoice',
  serviceBook,
  submitting = false,
}: PreInvoiceChecklistDialogProps) {
  const { t } = useTranslation('invoices');
  const { t: tChecklists } = useTranslation('checklists');
  const { t: tWorkOrders } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const updateServiceBook = useUpdateServiceBook();
  const [inspectionChecked, setInspectionChecked] = useState(false);
  const [partsConfirmed, setPartsConfirmed] = useState(false);
  const [serviceBookChecked, setServiceBookChecked] = useState(false);

  useEffect(() => {
    if (!open) return;
    setInspectionChecked(false);
    setPartsConfirmed(false);
    setServiceBookChecked(false);
  }, [open]);

  const isReadyStage = stage === 'ready_to_invoice';
  const askServiceBook = isReadyStage && serviceBook?.pending === true;
  const inspectionOk = finalInspectionCompleted || inspectionChecked;
  const partsOk = !isReadyStage || partsConfirmed;
  const serviceBookOk = !askServiceBook || serviceBookChecked || serviceBook?.required !== true;
  const busy = updateServiceBook.isPending || submitting;
  const canContinue = (isReadyStage || inspectionOk) && partsOk && serviceBookOk && !busy;

  const handleInspectionChange = async (checked: boolean) => {
    if (!checked || finalInspectionCompleted) return;
    setInspectionChecked(true);
    try {
      await updateServiceBook.mutateAsync({
        workOrderId,
        finalInspectionCompleted: true,
      });
    } catch (error) {
      setInspectionChecked(false);
      notify.error(tc('error'), describeApiError(error, tc, { fallback: tChecklists('errors.serviceBookFailed') }));
    }
  };

  const handleServiceBookChange = async (checked: boolean) => {
    if (!checked || serviceBookChecked) return;
    setServiceBookChecked(true);
    try {
      await updateServiceBook.mutateAsync({
        workOrderId,
        serviceBookChecked: true,
        serviceBookFilled: true,
      });
    } catch (error) {
      setServiceBookChecked(false);
      notify.error(tc('error'), describeApiError(error, tc, { fallback: tChecklists('errors.serviceBookFailed') }));
    }
  };

  const handleContinue = () => {
    if (!canContinue) return;
    onConfirm();
  };

  const title = isReadyStage ? t('preInvoiceChecklist.readyTitle') : t('preInvoiceChecklist.title');

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      mobileTitle={title}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent data-testid="pre-invoice-checklist-dialog" data-stage={stage}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isReadyStage
            ? t('preInvoiceChecklist.readyDescription')
            : t('preInvoiceChecklist.handoverDescription')}
        </Typography>
        {isReadyStage && (
          <Typography variant="body2" sx={{ mb: 2 }} data-testid="pre-invoice-line-summary">
            {t('preInvoiceChecklist.lineSummary', { laborCount, partsCount })}
          </Typography>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            data-testid="pre-invoice-final-inspection"
            control={
              <Checkbox
                checked={inspectionOk}
                disabled={inspectionOk || busy}
                onChange={(event) => {
                  void handleInspectionChange(event.target.checked);
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body2">{tChecklists('finalInspection.completed')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {finalInspectionCompleted
                    ? t('preInvoiceChecklist.finalInspectionAlreadyDone')
                    : t('preInvoiceChecklist.finalInspectionHint')}
                </Typography>
              </Box>
            }
          />
          {isReadyStage && (
            <FormControlLabel
              data-testid="pre-invoice-parts-labor-confirm"
              control={
                <Checkbox
                  checked={partsConfirmed}
                  onChange={(event) => setPartsConfirmed(event.target.checked)}
                />
              }
              label={
                <Typography variant="body2">{t('preInvoiceChecklist.partsAndLaborConfirm')}</Typography>
              }
            />
          )}
          {askServiceBook && (
            <FormControlLabel
              data-testid="pre-invoice-service-book"
              control={
                <Checkbox
                  checked={serviceBookChecked}
                  disabled={serviceBookChecked || busy}
                  onChange={(event) => {
                    void handleServiceBookChange(event.target.checked);
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t('preInvoiceChecklist.serviceBookConfirm')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {serviceBook?.required
                      ? t('preInvoiceChecklist.serviceBookRequiredHint')
                      : t('preInvoiceChecklist.serviceBookHint')}
                  </Typography>
                </Box>
              }
            />
          )}
        </Box>
        {isReadyStage && !inspectionOk && (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="pre-invoice-inspection-warning">
            {t('preInvoiceChecklist.finalInspectionLaterWarning')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {tc('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={!canContinue}
          data-testid="pre-invoice-continue"
          {...(busy
            ? { startIcon: <CircularProgress size={16} /> }
            : {})}
        >
          {isReadyStage ? tWorkOrders('serviceActions.readyToInvoice') : t('preInvoiceChecklist.continue')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
